import { Divider, Grid, Tab, Typography } from '@material-ui/core';
import { Ballot, ListAlt, Message as MessageIcon, NoteAdd, Person } from '@material-ui/icons';
import create from 'zustand';
import { AlertsTab } from '../../core/alerts/alerts-tab';
import { splitEntries } from '../../core/arrays';
import { ageInYears, formatLocalDate } from '../../core/date-time';
import { splitCamelCase, truncateSpan, withTooltip } from '../../core/format';
import { messageApi } from '../../core/message-api';
import { MessageEntry } from '../../core/messages';
import { orange } from '../../core/theme';
import { VisitContext, WorksheetApis } from '../../core/visit-context';
import { featureEnabled } from '../../core/workflow-api';
import { NotesTab } from '../common/notes';
import { FieldValue } from '../common/presentation';
import { TabPanel, WhiteBadge, WhiteTabs, tabIconStyle } from '../common/tabs';
import { RelatedWorksheets } from './related-worksheets';
import { VisitTrackingCodes } from './visit-tracking';
import { PreviousVisits, WorksheetSummary } from './worksheet-summary';

import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { incompleteAlerts } from '../../core/alerts/alerts-model';
import { alertsUpdated } from '../../core/alerts/alerts-api';
import { getPrimaryInsuranceLabel } from '../../core';

function VField({ label, value, style = undefined, truncate = NaN }) {
  return (
    <Grid container direction="column" style={style}>
      <Grid item>
        <Typography style={{ whiteSpace: 'nowrap' }} variant="body2" color="textSecondary">
          {label}
        </Typography>
      </Grid>
      <Grid item>
        <Typography color="textPrimary">{isNaN(truncate) ? value : truncateSpan(value, truncate + 'px')}</Typography>
      </Grid>
    </Grid>
  );
}

export function Demographics(ctx: VisitContext) {
  const { visit, arrival, disposition, injury, injuryDate, injuryState, config, readonly } = ctx;

  const { firstName, lastName, birthDate, gender } = visit.patient;
  const { accountNumber, mrn, registrationDate, dischargeDate, insurances, customFields, customFieldsArray } = visit;
  const ins = getPrimaryInsuranceLabel(insurances || []);

  const divider = (
    <div style={{ padding: '12px 0px 12px 0px' }}>
      <Divider orientation="horizontal" style={{ width: '312px' }} />
    </div>
  );

  const pairs = customFields ? splitEntries(customFields, 2, splitCamelCase) : null;

  const customPairs = customFieldsArray?.length ? customFieldsArray.map((obj) => splitEntries(obj, 2, splitCamelCase)).flat() : null;
  return (
    <div style={{ width: '100%', padding: '12px 24px 0 24px' }} id="field_item">
      <FieldValue label="Patient" value={lastName + ', ' + firstName} />
      <Grid container style={{ paddingTop: 12, flexWrap: 'nowrap', width: '100%' }} direction="row" spacing={2} justify="space-between" alignItems="center">
        <FieldValue label="Account" value={accountNumber} />
        <VField label="MRN" value={mrn} truncate={67} />
        <FieldValue label="Date of Birth" value={birthDate ? formatLocalDate(birthDate) : ''} />
        <FieldValue label="Age" value={birthDate ? ageInYears(birthDate) : ''} />
        <FieldValue label="Gen." value={(gender || '').charAt(0)} />
      </Grid>
      {divider}
      <VisitTrackingCodes readonly={readonly} visitId={visit.visitId} arrival={arrival} disposition={disposition} injury={injury} injuryDate={injuryDate} injuryState={injuryState} context={ctx} />
      {divider}
      <Grid container style={{ paddingTop: 12, paddingLeft: 8, flexWrap: 'nowrap' }} direction="row" spacing={2} justify="center" alignItems="center">
        <VField label="Registration Date" value={formatLocalDate(registrationDate)} />
        {dischargeDate && <VField label="Discharge Date" value={formatLocalDate(dischargeDate)} />}
      </Grid>
      <VField label="Insurance" value={ins} style={{ paddingTop: 16 }} />
      <Grid item style={{ height: 8 }} />
      <Grid
        container
        style={{ paddingTop: 0, paddingLeft: 0, maxHeight: '12vh', overflowY: 'auto', flexWrap: 'wrap' }}
        direction="row"
        spacing={1}
        justifyContent="flex-start"
        alignItems="flex-start"
      >
        {customPairs &&
          customPairs.map((v, i) => (
            <Grid item key={'c' + i}>
              <VField label={v[0][0]} value={v[0][1]} truncate={140} />
              {v[1] && <VField label={v[1][0]} value={v[1][1]} truncate={140} />}
            </Grid>
          ))}
        {pairs &&
          pairs.map((v, i) => (
            <Grid item key={'c' + i}>
              <VField label={v[0][0]} value={v[0][1]} truncate={140} />
              {v[1] && <VField label={v[1][0]} value={v[1][1]} truncate={140} />}
            </Grid>
          ))}
      </Grid>
    </div>
  );
}

export const visitDrawerTabs = create(() => ({
  index: 0,
}));

function AlertNumber({ apis }: { apis: WorksheetApis }) {
  const { alerts } = apis;
  const a = alerts((s) => s.alerts);
  const nAlerts = (a && incompleteAlerts(a).length) || 0;

  return withTooltip(
    'Alerts',
    <WhiteBadge
      badgeContent={nAlerts}
      invisible={nAlerts < 1}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
    >
      <ErrorOutlineIcon style={{ marginBottom: 7 }} />
    </WhiteBadge>
  );
}

let tabIndex = 0;
export function VisitDrawer(props?: VisitContext) {
  const { notesApi, worksheets, worksheetTypes, hospitalist, hospitalistContext, alertsV2, readonly, visit } = props;

  const value = visitDrawerTabs((s) => s.index);

  const onChange = (_: any, index: number) => {
    tabIndex = index;
    visitDrawerTabs.setState({ index });
  };

  alertsUpdated((s) => s.b);
  const coderNotesEnabled = visit.locationCode.includes('USACS') || featureEnabled('CoderNotes');

  notesApi.apis = worksheets;

  const openNotes = notesApi.useNotes((s) => s.openDialog);
  const onNoteClicked = () => {
    openNotes(worksheetTypes);
  };

  //TODO: worksheet selection api
  notesApi.useNotes((s) => s.notes);
  const nNotes = notesApi.useNotes((s) => s.nNotes(worksheetTypes));
  const nMessages = messageApi.data((s) => s.nMesssages);

  const showPreviousVisits = props.showPreviousVisits;

  let showAlerts = false;
  let worksheetApis = null;

  if (hospitalistContext && alertsV2) {
    worksheetApis = hospitalistContext.selectedApis();
    if (worksheetApis) {
      showAlerts = true;
    }
  } else {
    showAlerts = worksheets[0] && alertsV2;
    worksheetApis = worksheets[0];
  }

  const i = showAlerts ? 1 : 0;
  const alertsKey = worksheetApis?.context?.worksheet?.worksheetId;

  return (
    <>
      <div style={{ backgroundColor: orange, width: '100%' }}>
        <WhiteTabs centered value={value} onChange={onChange}>
          {showAlerts && <Tab id="alerts-tab" label={worksheetApis && <AlertNumber key={alertsKey} apis={worksheetApis} />} style={tabIconStyle} />}

          <Tab id="patient-info" label={withTooltip('Patient Information', <Person />)} style={tabIconStyle} />

          <Tab id="worksheet-summary" label={withTooltip('Worksheet Summaries', <ListAlt />)} style={tabIconStyle} />

          <Tab
            id="messages"
            label={withTooltip(
              'Messages',
              <WhiteBadge
                badgeContent={nMessages}
                invisible={nMessages < 1}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
              >
                <MessageIcon style={{ marginBottom: 7 }} />
              </WhiteBadge>
            )}
            style={tabIconStyle}
          />

          {coderNotesEnabled && (
            <Tab
              id="coder-notes"
              label={withTooltip(
                'Coder Notes',
                <WhiteBadge
                  onClick={onNoteClicked}
                  badgeContent={nNotes}
                  invisible={nNotes < 1}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                >
                  <NoteAdd style={{ marginBottom: 7 }} />
                </WhiteBadge>
              )}
              style={tabIconStyle}
            />
          )}
          {showPreviousVisits && <Tab id="previous-visits" label={withTooltip('Previous Visits', <Ballot />)} style={tabIconStyle} />}
        </WhiteTabs>
      </div>
      {showAlerts && (
        <TabPanel value={value} index={0}>
          <AlertsTab key={alertsKey} apis={worksheetApis} readonly={readonly} visit={visit} />
        </TabPanel>
      )}

      <TabPanel value={value} index={i}>
        <Demographics {...props} />
      </TabPanel>
      <TabPanel value={value} index={i + 1}>
        {hospitalist ? (
          <>
            <RelatedWorksheets {...props} />
          </>
        ) : (
          <WorksheetSummary {...props} />
        )}
      </TabPanel>
      <TabPanel value={value} index={i + 2}>
        <MessageEntry {...messageApi} />
      </TabPanel>

      {coderNotesEnabled && (
        <TabPanel value={value} index={i + 3}>
          <NotesTab api={notesApi} hospitalistContext={hospitalistContext} />
        </TabPanel>
      )}
      {showPreviousVisits && (
        <TabPanel value={value} index={i + 4}>
          <PreviousVisits {...props} />
        </TabPanel>
      )}
    </>
  );
}
